import { create } from 'zustand';
import {
  insertTile,
  moveTileTo,
  TileRows,
  TileTarget,
} from './grid-layout-math';
import {
  COMMAND_DASHBOARD_TILE_SIZE_PROFILES,
  COMMAND_DASHBOARD_TILE_LIBRARY,
  CommandDashboardTileKey,
} from './tile-config';

/**
 * Seeded once for an existing user (`isFirstGpanelLogin === false`) who has
 * never saved a dashboard view, so they land on a populated view instead of
 * the empty one a brand-new user starts with. Purely local until the user
 * explicitly saves it - see use-command-dashboard-actions.ts.
 */
export const DEFAULT_EXISTING_USER_TILE_KEYS: CommandDashboardTileKey[] = [
  'loginActivity',
  'rulesEngine',
  'activeUsers',
];

const validKeys = new Set(
  COMMAND_DASHBOARD_TILE_LIBRARY.map((tile) => tile.key)
);

const isValidKey = (key: unknown): key is CommandDashboardTileKey =>
  typeof key === 'string' && validKeys.has(key as CommandDashboardTileKey);

/**
 * insertTile/moveTileTo operate on plain keys here, so this resolves the
 * profile's row-unit cost from the tile library.
 */
const rowUnitsForKey = (key: CommandDashboardTileKey): number => {
  const tile = COMMAND_DASHBOARD_TILE_LIBRARY.find(
    (candidate) => candidate.key === key
  );
  return tile
    ? COMMAND_DASHBOARD_TILE_SIZE_PROFILES[tile.sizeProfile].rowUnits
    : 1;
};

/**
 * Drops invalid keys and duplicates, splits rows over three units, and removes
 * rows left empty. Standard tiles cost one unit and wide tiles cost two.
 */
export function normalizeRows(rows: TileRows): TileRows {
  const seen = new Set<CommandDashboardTileKey>();
  const normalized: TileRows = [];

  rows.forEach((row) => {
    const cleaned = row.filter((key) => {
      if (!isValidKey(key) || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    let chunk: CommandDashboardTileKey[] = [];
    let usedUnits = 0;
    cleaned.forEach((key) => {
      const units = rowUnitsForKey(key);
      if (usedUnits + units > 3) {
        normalized.push(chunk);
        chunk = [];
        usedUnits = 0;
      }
      chunk.push(key);
      usedUnits += units;
    });
    if (chunk.length > 0) {
      normalized.push(chunk);
    }
  });

  return normalized;
}

const withoutKey = (rows: TileRows, key: CommandDashboardTileKey): TileRows =>
  rows
    .map((row) => row.filter((activeKey) => activeKey !== key))
    .filter((row) => row.length > 0);

const containsKey = (rows: TileRows, key: CommandDashboardTileKey): boolean =>
  rows.some((row) => row.includes(key));

interface CommandDashboardTilesState {
  /**
   * The working layout: in-memory only, touched by every add/move/remove.
   * Nothing here is persisted until the user explicitly saves a dashboard
   * view (see dashboard-view-menu-button.tsx / save-dashboard-view-dialog.tsx)
   * - there is no local storage or other implicit persistence.
   */
  tileRows: TileRows;
  /**
   * Tile currently being dragged out of the tile library. Transient, so it is
   * deliberately not part of any saved view. `dataTransfer` payloads are
   * unreadable during `dragover`, so the grid reads this to size the drop
   * placeholder.
   */
  draggingTileKey: CommandDashboardTileKey | null;
  addTile: (key: CommandDashboardTileKey) => void;
  placeTile: (key: CommandDashboardTileKey, target: TileTarget) => void;
  moveTile: (key: CommandDashboardTileKey, target: TileTarget) => void;
  removeTile: (key: CommandDashboardTileKey) => void;
  setDraggingTileKey: (key: CommandDashboardTileKey | null) => void;
  /**
   * Replaces the working layout wholesale - used when the user's saved
   * dashboard view is first loaded, when they switch to a different saved
   * view, and after a fresh save.
   */
  hydrate: (tileRows: TileRows) => void;
}

export const useCommandDashboardTilesStore = create<CommandDashboardTilesState>(
  (set, get) => ({
    tileRows: [],
    draggingTileKey: null,

    addTile: (key) => {
      const current = get().tileRows;
      if (containsKey(current, key)) {
        return;
      }
      set({ tileRows: [...current.map((row) => [...row]), [key]] });
    },

    placeTile: (key, target) => {
      const current = get().tileRows;
      if (containsKey(current, key)) {
        return;
      }
      set({ tileRows: insertTile(current, key, target, rowUnitsForKey) });
    },

    moveTile: (key, target) => {
      const current = get().tileRows;
      if (containsKey(current, key)) {
        set({ tileRows: moveTileTo(current, key, target, rowUnitsForKey) });
      }
    },

    removeTile: (key) => set({ tileRows: withoutKey(get().tileRows, key) }),

    setDraggingTileKey: (key) => set({ draggingTileKey: key }),

    hydrate: (rows) => set({ tileRows: normalizeRows(rows) }),
  })
);
